import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, from, Observable, of} from 'rxjs';
import {catchError, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';

export type PromoMode = 'none' | 'card' | 'image';

export interface BrandTheme {
  fontColor: string;
  buttonColor: string;
  iconColor: string;
  marketplaceName: string;
  /** CSS font-family stack for brand wordmark. */
  fontFamily: string;
  /**
   * Optional header/footer logo image (relative path or https).
   * Marketplace-only — seller shop themes never override this.
   */
  logoUrl: string;
  /** Seller shopfront jumbo / hero image (relative path or https). */
  jumboImageUrl: string;
  /** Profile banner background when no jumbo image. */
  jumboBackgroundColor: string;
  /** Profile landing page hero copy. */
  heroTitle: string;
  heroHighlight: string;
  heroSubtitle: string;
  heroCtaLabel: string;
  /**
   * Optional hero CTA destination (e.g. /electronics). Empty = scroll to categories
   * on marketplace home, or listings on a seller shopfront.
   */
  browseLink: string;
  /**
   * Side promo on seller shopfront when no jumbo: none | card | image.
   * Ignored when jumboImageUrl is set.
   */
  promoMode: PromoMode;
  promoImageUrl: string;
  /** Soft-blend overall opacity 40–100. */
  promoBlendOpacity: number;
  /** Outer edge fade width percent 0–40. */
  promoBlendFade: number;
  promoIcon: string;
  promoTitle: string;
  promoSubtitle: string;
  promoCtaLabel: string;
  /** Listings section heading on seller shopfront. */
  listingsTitle: string;
  listingsSubtitle: string;
  /** Ordered shop page sections (hero, latest, banner, richtext). */
  sections: HomePageSection[];
  /** Desktop product grid columns on seller shopfront (2–6). */
  listingsPerRow: number;
}

export type HomeSectionType = 'hero' | 'categories' | 'latest' | 'banner' | 'richtext';

export interface HomePageSection {
  id: string;
  type: HomeSectionType;
  enabled: boolean;
  props: Record<string, string>;
}

export interface HomePageConfig {
  heroTitle: string;
  heroHighlight: string;
  heroSubtitle: string;
  heroCtaLabel: string;
  /** Marketplace home jumbo / hero image (relative path or https). */
  heroImageUrl: string;
  /** Hero background when no jumbo image; empty = default gradient. */
  heroBackgroundColor: string;
  /**
   * Side promo when no jumbo hero: none | card (text/icon) | image (soft blend).
   * Ignored when heroImageUrl is set.
   */
  promoMode: PromoMode;
  /** Soft side image when promoMode is image. */
  promoImageUrl: string;
  /** Soft-blend overall opacity 40–100. */
  promoBlendOpacity: number;
  /** Outer edge fade width percent 0–40. */
  promoBlendFade: number;
  promoIcon: string;
  promoTitle: string;
  promoSubtitle: string;
  promoCtaLabel: string;
  justAddedTitle: string;
  justAddedSubtitle: string;
  viewAllLabel: string;
  categoriesTitle: string;
  categoriesSubtitle: string;
  browseLink: string;
  currencyCode: string;
  /** Top-level category URL slugs to show on the home strip; empty = all. */
  featuredCategoryUrls: string[];
  /** Desktop product grid columns for Just Added (2–6). */
  listingsPerRow: number;
  /** Ordered page sections (drag-reorder in admin). */
  sections: HomePageSection[];
}

export const HOME_SECTION_LABELS: Record<HomeSectionType, string> = {
  hero: 'Hero',
  categories: 'Categories',
  latest: 'Just added',
  banner: 'Banner',
  richtext: 'Text block',
};

/** Seller shopfront section labels (no marketplace categories strip). */
export const SHOP_SECTION_LABELS: Record<'hero' | 'latest' | 'banner' | 'richtext', string> = {
  hero: 'Hero',
  latest: 'Listings',
  banner: 'Banner',
  richtext: 'Text block',
};

export const DEFAULT_HOME_SECTIONS: HomePageSection[] = [
  {id: 'hero', type: 'hero', enabled: true, props: {}},
  {id: 'categories', type: 'categories', enabled: true, props: {}},
  {id: 'latest', type: 'latest', enabled: true, props: {}},
];

export const DEFAULT_SHOP_SECTIONS: HomePageSection[] = [
  {id: 'hero', type: 'hero', enabled: true, props: {}},
  {id: 'latest', type: 'latest', enabled: true, props: {}},
];

export const DEFAULT_BANNER_PROPS: Record<string, string> = {
  title: 'Featured',
  subtitle: 'A highlight from this marketplace.',
  ctaLabel: 'Learn more',
  ctaLink: '',
  backgroundColor: '#2563eb',
};

export const DEFAULT_RICHTEXT_PROPS: Record<string, string> = {
  title: 'About',
  body: 'Tell visitors what makes this marketplace special.',
};

export interface BrandingSettings {
  theme: BrandTheme;
  home: HomePageConfig;
}

export interface ProfileThemeResponse {
  profileUrl: string;
  profileName: string;
  /** Optional seller profile picture (relative path or https). */
  avatarUrl: string;
  theme: BrandTheme;
}

export interface BrandFontOption {
  id: string;
  label: string;
  css: string;
}

export const BRAND_FONT_OPTIONS: BrandFontOption[] = [
  {id: 'source-sans', label: 'Source Sans Pro', css: "'Source Sans Pro', 'Helvetica Neue', Arial, sans-serif"},
  {id: 'poppins', label: 'Poppins', css: "'Poppins', 'Helvetica Neue', Arial, sans-serif"},
  {id: 'helvetica', label: 'Helvetica Neue', css: "'Helvetica Neue', Helvetica, Arial, sans-serif"},
  {id: 'georgia', label: 'Georgia', css: "Georgia, 'Times New Roman', serif"},
  {id: 'system', label: 'System UI', css: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"},
];

export const DEFAULT_BRAND_THEME: BrandTheme = {
  fontColor: '#2563eb',
  buttonColor: '#2563eb',
  iconColor: '#2563eb',
  marketplaceName: 'Marketplace',
  fontFamily: BRAND_FONT_OPTIONS[0].css,
  logoUrl: '',
  jumboImageUrl: '',
  jumboBackgroundColor: '',
  heroTitle: 'Welcome to',
  heroHighlight: '',
  heroSubtitle: 'Discover great listings and unbeatable offers from trusted sellers in your community.',
  heroCtaLabel: 'Explore Listings',
  browseLink: '',
  promoMode: 'none',
  promoImageUrl: '',
  promoBlendOpacity: 92,
  promoBlendFade: 22,
  promoIcon: 'local_offer',
  promoTitle: 'Featured Offers',
  promoSubtitle: 'Handpicked deals from this shop.',
  promoCtaLabel: 'View listings',
  listingsTitle: 'Latest listings',
  listingsSubtitle: 'Discover our newest listings from this shop',
  sections: DEFAULT_SHOP_SECTIONS.map(s => ({...s, props: {...s.props}})),
  listingsPerRow: 4,
};

export const DEFAULT_HOME_PAGE: HomePageConfig = {
  heroTitle: 'Everything you need,',
  heroHighlight: 'all in one place',
  heroSubtitle: 'Discover great listings and unbeatable offers from trusted sellers in your community.',
  heroCtaLabel: 'Start Browsing',
  heroImageUrl: '',
  heroBackgroundColor: '',
  promoMode: 'card',
  promoImageUrl: '',
  promoBlendOpacity: 92,
  promoBlendFade: 22,
  promoIcon: 'local_offer',
  promoTitle: 'Featured Offers',
  promoSubtitle: 'Handpicked deals, refreshed regularly.',
  promoCtaLabel: 'View Offers',
  justAddedTitle: 'Just Added',
  justAddedSubtitle: 'The newest listings from our community',
  viewAllLabel: 'View all listings',
  categoriesTitle: 'Browse categories',
  categoriesSubtitle: 'Explore what this marketplace sells',
  browseLink: '',
  currencyCode: 'EUR',
  featuredCategoryUrls: [],
  listingsPerRow: 4,
  sections: DEFAULT_HOME_SECTIONS.map(s => ({...s, props: {...s.props}})),
};

export const LISTINGS_PER_ROW_OPTIONS: number[] = [2, 3, 4, 5, 6];

export const CURRENCY_OPTIONS: Array<{code: string; label: string}> = [
  {code: 'EUR', label: 'Euro (EUR)'},
  {code: 'GBP', label: 'Pound sterling (GBP)'},
  {code: 'USD', label: 'US dollar (USD)'},
  {code: 'AUD', label: 'Australian dollar (AUD)'},
  {code: 'CAD', label: 'Canadian dollar (CAD)'},
  {code: 'CHF', label: 'Swiss franc (CHF)'},
  {code: 'SEK', label: 'Swedish krona (SEK)'},
  {code: 'NOK', label: 'Norwegian krone (NOK)'},
  {code: 'DKK', label: 'Danish krone (DKK)'},
  {code: 'PLN', label: 'Polish złoty (PLN)'},
];

@Injectable({providedIn: 'root'})
export class BrandThemeService {
  private readonly themeSubject = new BehaviorSubject<BrandTheme>({...DEFAULT_BRAND_THEME});
  readonly theme$ = this.themeSubject.asObservable();
  private readonly homeSubject = new BehaviorSubject<HomePageConfig>({...DEFAULT_HOME_PAGE});
  readonly home$ = this.homeSubject.asObservable();
  private load$?: Observable<BrandTheme>;
  /** Marketplace-wide theme from /api/config (restored when leaving a profile URL). */
  private marketplaceTheme: BrandTheme = {...DEFAULT_BRAND_THEME};
  /** Active seller shopfront URL slug, or null when browsing the whole marketplace. */
  private activeProfileUrl: string | null = null;

  constructor(private http: HttpClient) {
    this.applyTheme(DEFAULT_BRAND_THEME);
  }

  /** Current theme snapshot. */
  get theme(): BrandTheme {
    return this.themeSubject.value;
  }

  get home(): HomePageConfig {
    return this.homeSubject.value;
  }

  /** Load theme + home from public config and apply CSS variables on :root. */
  ensureLoaded(): Observable<BrandTheme> {
    if (!this.load$) {
      this.load$ = from(environment).pipe(
        switchMap((env: any) => this.http.get<any>(`${env.backendURL}/api/config`)),
        map(config => {
          const theme = this.normalizeTheme(config?.theme);
          const home = this.normalizeHome(config?.home);
          this.homeSubject.next(home);
          return theme;
        }),
        catchError(() => {
          this.homeSubject.next({...DEFAULT_HOME_PAGE});
          return of({...DEFAULT_BRAND_THEME});
        }),
        tap(theme => {
          this.marketplaceTheme = theme;
          if (!this.activeProfileUrl) {
            this.applyTheme(theme);
          }
        }),
        shareReplay(1)
      );
    }
    return this.load$;
  }

  /** Admin: fetch current branding (theme + home). */
  fetchAdminBranding(): Observable<BrandingSettings> {
    return from(environment).pipe(
      switchMap((env: any) => this.http.get<BrandingSettings>(`${env.backendURL}/api/admin/branding`)),
      map(settings => this.normalizeSettings(settings))
    );
  }

  /** @deprecated use fetchAdminBranding */
  fetchAdminTheme(): Observable<BrandTheme> {
    return this.fetchAdminBranding().pipe(map(s => s.theme));
  }

  /** Admin: save branding and re-apply CSS vars immediately. */
  saveBranding(settings: BrandingSettings): Observable<BrandingSettings> {
    const body = this.normalizeSettings(settings);
    return from(environment).pipe(
      switchMap((env: any) => this.http.put<BrandingSettings>(`${env.backendURL}/api/admin/branding`, body)),
      map(saved => this.normalizeSettings(saved)),
      tap(saved => {
        this.marketplaceTheme = saved.theme;
        this.homeSubject.next(saved.home);
        this.load$ = of(saved.theme).pipe(shareReplay(1));
        if (this.activeProfileUrl) {
          // Refresh marketplace identity/logo; keep active seller colour overlay.
          this.applyTheme(this.withProfileColourOverlay(saved.theme, this.themeSubject.value));
        } else {
          this.applyTheme(saved.theme);
        }
      })
    );
  }

  /** @deprecated use saveBranding */
  saveTheme(theme: BrandTheme): Observable<BrandTheme> {
    return this.saveBranding({theme, home: this.home}).pipe(map(s => s.theme));
  }

  /**
   * Overlay seller shopfront colours while browsing /{profileUrl}/...
   * Marketplace name, font, and logo stay fixed in the header/footer.
   * Restored via clearProfileTheme() when leaving profile routes.
   */
  applyProfileTheme(profileUrl: string): Observable<BrandTheme> {
    const slug = (profileUrl || '').trim().toLowerCase();
    if (!slug) {
      this.clearProfileTheme();
      return of(this.marketplaceTheme);
    }
    return this.fetchPublicProfileTheme(slug).pipe(
      tap(res => {
        this.activeProfileUrl = slug;
        this.applyTheme(this.withProfileColourOverlay(this.marketplaceTheme, res.theme));
      }),
      map(res => this.withProfileColourOverlay(this.marketplaceTheme, res.theme))
    );
  }

  /** Public profile shopfront theme for /{profileUrl} (hero, colours). */
  fetchPublicProfileTheme(profileUrl: string): Observable<ProfileThemeResponse> {
    const slug = (profileUrl || '').trim().toLowerCase();
    if (!slug) {
      return of({
        profileUrl: '',
        profileName: '',
        avatarUrl: '',
        theme: {...DEFAULT_BRAND_THEME},
      });
    }
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.get<ProfileThemeResponse>(`${env.backendURL}/api/profiles/${encodeURIComponent(slug)}/theme`)),
      map(res => this.normalizeProfileThemeResponse(res, slug)),
      catchError(() => of({
        profileUrl: slug,
        profileName: '',
        avatarUrl: '',
        theme: {...DEFAULT_BRAND_THEME},
      }))
    );
  }

  /** Restore marketplace branding after leaving a profile shopfront URL. */
  clearProfileTheme(): void {
    if (!this.activeProfileUrl) {
      return;
    }
    this.activeProfileUrl = null;
    this.applyTheme(this.marketplaceTheme);
  }

  /** Seller settings: load own shopfront theme. */
  fetchSellerTheme(): Observable<ProfileThemeResponse> {
    return from(environment).pipe(
      switchMap((env: any) => this.http.get<ProfileThemeResponse>(`${env.backendURL}/api/seller/theme`)),
      map(res => this.normalizeProfileThemeResponse(res)),
      catchError(() => of({
        profileUrl: '',
        profileName: '',
        avatarUrl: '',
        theme: {...DEFAULT_BRAND_THEME},
      }))
    );
  }

  /** Seller settings: save shopfront theme; applies immediately if that profile is active. */
  saveSellerTheme(theme: BrandTheme): Observable<ProfileThemeResponse> {
    const body = this.normalizeTheme(theme);
    return from(environment).pipe(
      switchMap((env: any) => this.http.put<ProfileThemeResponse>(`${env.backendURL}/api/seller/theme`, body)),
      map(res => this.normalizeProfileThemeResponse(res)),
      tap(res => {
        if (this.activeProfileUrl && res.profileUrl
            && this.activeProfileUrl === res.profileUrl.toLowerCase()) {
          this.applyTheme(this.withProfileColourOverlay(this.marketplaceTheme, res.theme));
        }
      })
    );
  }

  /** Seller: upload profile avatar (PNG/JPEG/WebP/SVG); returns relative path. */
  uploadSellerAvatar(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<{url: string}>(`${env.backendURL}/api/seller/avatar`, formData)),
      map(res => this.imageUrlOrEmpty(res?.url))
    );
  }

  /** Seller: clear profile avatar. */
  clearSellerAvatar(): Observable<ProfileThemeResponse> {
    return from(environment).pipe(
      switchMap((env: any) => this.http.delete<ProfileThemeResponse>(`${env.backendURL}/api/seller/avatar`)),
      map(res => this.normalizeProfileThemeResponse(res))
    );
  }

  /** Admin: upload marketplace home jumbo/hero image; returns relative path. */
  uploadHeroImage(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<{url: string}>(`${env.backendURL}/api/admin/branding/hero-image`, formData)),
      map(res => this.imageUrlOrEmpty(res?.url))
    );
  }

  /** Admin: upload soft side promo image for non-jumbo hero; returns relative path. */
  uploadPromoImage(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<{url: string}>(`${env.backendURL}/api/admin/branding/promo-image`, formData)),
      map(res => this.imageUrlOrEmpty(res?.url))
    );
  }

  /** Admin: upload marketplace header logo; returns relative path for theme.logoUrl. */
  uploadLogoImage(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<{url: string}>(`${env.backendURL}/api/admin/branding/logo-image`, formData)),
      map(res => this.imageUrlOrEmpty(res?.url))
    );
  }

  /** Seller: upload shop jumbo image; returns relative path. */
  uploadSellerJumboImage(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<{url: string}>(`${env.backendURL}/api/seller/theme/jumbo-image`, formData)),
      map(res => this.imageUrlOrEmpty(res?.url))
    );
  }

  /** Seller: upload soft side promo image; returns relative path. */
  uploadSellerPromoImage(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<{url: string}>(`${env.backendURL}/api/seller/theme/promo-image`, formData)),
      map(res => this.imageUrlOrEmpty(res?.url))
    );
  }

  /**
   * Resolve a stored branding image path for display.
   * Absolute http(s) URLs pass through; relative paths use imagesBaseUrl.
   */
  resolveMediaUrl(pathOrUrl: string | null | undefined, imagesBaseUrl?: string | null): string {
    const raw = (pathOrUrl || '').trim();
    if (!raw) {
      return '';
    }
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }
    const base = (imagesBaseUrl || '').replace(/\/+$/, '');
    if (!base) {
      return raw.startsWith('/') ? raw : `/${raw}`;
    }
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return `${base}${path}`;
  }

  applyTheme(theme: BrandTheme): void {
    const next = this.normalizeTheme(theme);
    this.themeSubject.next(next);
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    root.style.setProperty('--brand-accent', next.fontColor);
    root.style.setProperty('--brand-font', next.fontColor);
    root.style.setProperty('--brand-button', next.buttonColor);
    root.style.setProperty('--brand-icon', next.iconColor);
    root.style.setProperty('--brand-font-family', next.fontFamily);
    root.style.setProperty('--brand-accent-hover', `color-mix(in srgb, ${next.fontColor} 85%, black)`);
    root.style.setProperty('--brand-accent-soft', `color-mix(in srgb, ${next.fontColor} 10%, white)`);
    root.style.setProperty('--brand-button-hover', `color-mix(in srgb, ${next.buttonColor} 85%, black)`);
    root.style.setProperty('--brand-button-soft', `color-mix(in srgb, ${next.buttonColor} 10%, white)`);
    root.style.setProperty('--brand-icon-soft', `color-mix(in srgb, ${next.iconColor} 10%, white)`);
  }

  fontOptionId(css: string | null | undefined): string {
    const match = BRAND_FONT_OPTIONS.find(opt => opt.css === (css || '').trim());
    return match?.id || BRAND_FONT_OPTIONS[0].id;
  }

  fontCssForId(id: string): string {
    return BRAND_FONT_OPTIONS.find(opt => opt.id === id)?.css || DEFAULT_BRAND_THEME.fontFamily;
  }

  brandInitial(name?: string | null): string {
    const trimmed = (name || DEFAULT_BRAND_THEME.marketplaceName).trim();
    return (trimmed.charAt(0) || 'M').toUpperCase();
  }

  normalizeSettings(raw: Partial<BrandingSettings> | null | undefined): BrandingSettings {
    return {
      theme: this.normalizeTheme(raw?.theme),
      home: this.normalizeHome(raw?.home),
    };
  }

  private normalizeProfileThemeResponse(
    res: Partial<ProfileThemeResponse> | null | undefined,
    fallbackSlug = '',
  ): ProfileThemeResponse {
    return {
      profileUrl: res?.profileUrl || fallbackSlug,
      profileName: res?.profileName || '',
      avatarUrl: this.imageUrlOrEmpty(res?.avatarUrl),
      theme: this.normalizeTheme(res?.theme),
    };
  }

  /**
   * Keep marketplace identity (name, font, logo); overlay seller accent/button/icon colours.
   */
  private withProfileColourOverlay(marketplace: BrandTheme, profile: BrandTheme): BrandTheme {
    const base = this.normalizeTheme(marketplace);
    const colours = this.normalizeTheme(profile);
    return this.normalizeTheme({
      ...base,
      fontColor: colours.fontColor,
      buttonColor: colours.buttonColor,
      iconColor: colours.iconColor,
    });
  }

  private normalizeTheme(raw: Partial<BrandTheme> | null | undefined): BrandTheme {
    return {
      fontColor: this.hexOrDefault(raw?.fontColor, DEFAULT_BRAND_THEME.fontColor),
      buttonColor: this.hexOrDefault(raw?.buttonColor, DEFAULT_BRAND_THEME.buttonColor),
      iconColor: this.hexOrDefault(raw?.iconColor, DEFAULT_BRAND_THEME.iconColor),
      marketplaceName: this.nameOrDefault(raw?.marketplaceName),
      fontFamily: this.fontOrDefault(raw?.fontFamily),
      logoUrl: this.imageUrlOrEmpty(raw?.logoUrl),
      jumboImageUrl: this.imageUrlOrEmpty(raw?.jumboImageUrl),
      jumboBackgroundColor: this.optionalHexOrEmpty(raw?.jumboBackgroundColor),
      heroTitle: this.copyOrDefault(raw?.heroTitle, DEFAULT_BRAND_THEME.heroTitle, 160),
      heroHighlight: this.copyOrDefault(raw?.heroHighlight, DEFAULT_BRAND_THEME.heroHighlight, 160),
      heroSubtitle: this.copyOrDefault(raw?.heroSubtitle, DEFAULT_BRAND_THEME.heroSubtitle, 280),
      heroCtaLabel: this.copyOrDefault(raw?.heroCtaLabel, DEFAULT_BRAND_THEME.heroCtaLabel, 48),
      browseLink: this.browseLinkOrDefault(raw?.browseLink),
      promoMode: this.promoModeOrDefault(raw?.promoMode, DEFAULT_BRAND_THEME.promoMode),
      promoImageUrl: this.imageUrlOrEmpty(raw?.promoImageUrl),
      promoBlendOpacity: this.promoBlendOpacityOrDefault(raw?.promoBlendOpacity),
      promoBlendFade: this.promoBlendFadeOrDefault(raw?.promoBlendFade),
      promoIcon: this.iconOrDefault(raw?.promoIcon, DEFAULT_BRAND_THEME.promoIcon),
      promoTitle: this.copyOrDefault(raw?.promoTitle, DEFAULT_BRAND_THEME.promoTitle, 160),
      promoSubtitle: this.copyOrDefault(raw?.promoSubtitle, DEFAULT_BRAND_THEME.promoSubtitle, 280),
      promoCtaLabel: this.copyOrDefault(raw?.promoCtaLabel, DEFAULT_BRAND_THEME.promoCtaLabel, 48),
      listingsTitle: this.copyOrDefault(raw?.listingsTitle, DEFAULT_BRAND_THEME.listingsTitle, 160),
      listingsSubtitle: this.copyOrDefault(raw?.listingsSubtitle, DEFAULT_BRAND_THEME.listingsSubtitle, 280),
      sections: this.normalizeShopSections(raw?.sections),
      listingsPerRow: this.listingsPerRowOrDefault(raw?.listingsPerRow, DEFAULT_BRAND_THEME.listingsPerRow),
    };
  }

  normalizeHome(raw: Partial<HomePageConfig> | null | undefined): HomePageConfig {
    return {
      heroTitle: this.copyOrDefault(raw?.heroTitle, DEFAULT_HOME_PAGE.heroTitle, 160),
      heroHighlight: this.copyOrDefault(raw?.heroHighlight, DEFAULT_HOME_PAGE.heroHighlight, 160),
      heroSubtitle: this.copyOrDefault(raw?.heroSubtitle, DEFAULT_HOME_PAGE.heroSubtitle, 280),
      heroCtaLabel: this.copyOrDefault(raw?.heroCtaLabel, DEFAULT_HOME_PAGE.heroCtaLabel, 48),
      heroImageUrl: this.imageUrlOrEmpty(raw?.heroImageUrl),
      heroBackgroundColor: this.optionalHexOrEmpty(raw?.heroBackgroundColor),
      promoMode: this.promoModeOrDefault(raw?.promoMode),
      promoImageUrl: this.imageUrlOrEmpty(raw?.promoImageUrl),
      promoBlendOpacity: this.promoBlendOpacityOrDefault(raw?.promoBlendOpacity),
      promoBlendFade: this.promoBlendFadeOrDefault(raw?.promoBlendFade),
      promoIcon: this.iconOrDefault(raw?.promoIcon),
      promoTitle: this.copyOrDefault(raw?.promoTitle, DEFAULT_HOME_PAGE.promoTitle, 160),
      promoSubtitle: this.copyOrDefault(raw?.promoSubtitle, DEFAULT_HOME_PAGE.promoSubtitle, 280),
      promoCtaLabel: this.copyOrDefault(raw?.promoCtaLabel, DEFAULT_HOME_PAGE.promoCtaLabel, 48),
      justAddedTitle: this.copyOrDefault(raw?.justAddedTitle, DEFAULT_HOME_PAGE.justAddedTitle, 160),
      justAddedSubtitle: this.copyOrDefault(raw?.justAddedSubtitle, DEFAULT_HOME_PAGE.justAddedSubtitle, 280),
      viewAllLabel: this.copyOrDefault(raw?.viewAllLabel, DEFAULT_HOME_PAGE.viewAllLabel, 48),
      categoriesTitle: this.copyOrDefault(raw?.categoriesTitle, DEFAULT_HOME_PAGE.categoriesTitle, 160),
      categoriesSubtitle: this.copyOrDefault(raw?.categoriesSubtitle, DEFAULT_HOME_PAGE.categoriesSubtitle, 280),
      browseLink: this.browseLinkOrDefault(raw?.browseLink),
      currencyCode: this.currencyOrDefault(raw?.currencyCode),
      featuredCategoryUrls: this.featuredUrlsOrDefault(raw?.featuredCategoryUrls),
      listingsPerRow: this.listingsPerRowOrDefault(raw?.listingsPerRow, DEFAULT_HOME_PAGE.listingsPerRow),
      sections: this.sectionsOrDefault(raw?.sections),
    };
  }

  enabledSections(home: HomePageConfig | null | undefined): HomePageSection[] {
    return (home?.sections || DEFAULT_HOME_SECTIONS).filter(s => s.enabled);
  }

  createSection(type: 'banner' | 'richtext'): HomePageSection {
    const id = `${type}-${Date.now().toString(36)}`;
    if (type === 'banner') {
      return {id, type, enabled: true, props: {...DEFAULT_BANNER_PROPS}};
    }
    return {id, type, enabled: true, props: {...DEFAULT_RICHTEXT_PROPS}};
  }

  createShopSection(type: 'banner' | 'richtext'): HomePageSection {
    const section = this.createSection(type);
    if (type === 'banner') {
      return {
        ...section,
        props: {
          ...section.props,
          subtitle: 'A highlight from this shop.',
          ctaLabel: 'View listings',
        },
      };
    }
    return {
      ...section,
      props: {
        ...section.props,
        body: 'Tell visitors what makes this shop special.',
      },
    };
  }

  enabledShopSections(theme: BrandTheme | null | undefined): HomePageSection[] {
    return this.normalizeShopSections(theme?.sections).filter(s => s.enabled);
  }

  private normalizeShopSections(raw: HomePageSection[] | null | undefined): HomePageSection[] {
    const allowed = new Set(['hero', 'latest', 'banner', 'richtext']);
    const unique = new Set(['hero', 'latest']);
    const seenUnique = new Set<string>();
    const seenIds = new Set<string>();
    const out: HomePageSection[] = [];
    for (const section of raw || []) {
      if (out.length >= 12) {
        break;
      }
      const type = (section?.type || '').trim().toLowerCase() as HomeSectionType;
      if (!allowed.has(type)) {
        continue;
      }
      if (unique.has(type) && seenUnique.has(type)) {
        continue;
      }
      if (unique.has(type)) {
        seenUnique.add(type);
      }
      let id = (section?.id || '').trim().toLowerCase();
      if (!/^[a-z0-9_-]{1,40}$/.test(id) || seenIds.has(id)) {
        id = `${type}-${out.length + 1}`;
      }
      seenIds.add(id);
      out.push({
        id,
        type,
        enabled: section?.enabled !== false,
        props: {...(section?.props || {})},
      });
    }
    for (const required of ['hero', 'latest'] as const) {
      if (!seenUnique.has(required)) {
        out.push({id: required, type: required, enabled: true, props: {}});
      }
    }
    return out.length ? out : DEFAULT_SHOP_SECTIONS.map(s => ({...s, props: {...s.props}}));
  }

  private copyOrDefault(value: string | null | undefined, fallback: string, maxLen: number): string {
    const trimmed = (value || '').trim().replace(/\s+/g, ' ');
    if (!trimmed) {
      return fallback;
    }
    return trimmed.slice(0, maxLen);
  }

  private iconOrDefault(value: string | null | undefined, fallback?: string): string {
    const trimmed = (value || '').trim().toLowerCase().replace(/-/g, '_');
    if (/^[a-z0-9_]{2,48}$/.test(trimmed)) {
      return trimmed;
    }
    return fallback || DEFAULT_HOME_PAGE.promoIcon;
  }

  private promoModeOrDefault(value: string | null | undefined, fallback?: PromoMode): PromoMode {
    const mode = (value || '').trim().toLowerCase();
    if (mode === 'none' || mode === 'card' || mode === 'image') {
      return mode;
    }
    return fallback || DEFAULT_HOME_PAGE.promoMode;
  }

  promoBlendOpacityOrDefault(value: number | string | null | undefined, fallback = 92): number {
    const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    if (!Number.isFinite(n) || n < 40 || n > 100) {
      return fallback >= 40 && fallback <= 100 ? fallback : 92;
    }
    return Math.round(n);
  }

  promoBlendFadeOrDefault(value: number | string | null | undefined, fallback = 22): number {
    const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    if (!Number.isFinite(n) || n < 0 || n > 40) {
      return fallback >= 0 && fallback <= 40 ? fallback : 22;
    }
    return Math.round(n);
  }

  /** CSS custom properties for soft-blend promo rendering / editor preview. */
  promoBlendStyle(opacity?: number | null, fade?: number | null): {[key: string]: string} {
    const o = this.promoBlendOpacityOrDefault(opacity);
    const f = this.promoBlendFadeOrDefault(fade);
    const fadeY = Math.round(f * 0.82);
    return {
      '--promo-blend-opacity': String(o / 100),
      '--promo-fade': `${f}%`,
      '--promo-fade-y': `${fadeY}%`,
    };
  }

  /** Normalize a relative browse path; empty means use page default (categories/listings scroll). */
  browseLinkOrDefault(value: string | null | undefined): string {
    const trimmed = (value || '').trim();
    if (!trimmed || trimmed === '/' || trimmed.toLowerCase() === '/home') {
      return '';
    }
    const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (!/^(\/[a-zA-Z0-9._~-]+)+$/.test(withSlash) || withSlash.length > 120) {
      return '';
    }
    return withSlash;
  }

  private currencyOrDefault(value: string | null | undefined): string {
    const code = (value || '').trim().toUpperCase();
    if (CURRENCY_OPTIONS.some(opt => opt.code === code)) {
      return code;
    }
    if (/^[A-Z]{3}$/.test(code)) {
      return code;
    }
    return DEFAULT_HOME_PAGE.currencyCode;
  }

  listingsPerRowOrDefault(value: number | string | null | undefined, fallback = 4): number {
    const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    if (!Number.isFinite(n) || n < 2 || n > 6) {
      return fallback >= 2 && fallback <= 6 ? fallback : 4;
    }
    return Math.round(n);
  }

  /**
   * Product grid layout: use configured columns as a max.
   * Fewer items than that → fewer columns so cards spread across the row.
   * A single item is centered at a readable max width (desktop).
   * Sets --listings-per-row for mobile CSS to cap at 2 without forcing empty tracks.
   */
  productsGridStyle(
    perRow: number | null | undefined,
    itemCount?: number | null,
  ): {[key: string]: string} {
    const configured = this.listingsPerRowOrDefault(perRow, 4);
    const count = itemCount == null || !Number.isFinite(itemCount) ? -1 : Math.max(0, Math.floor(itemCount));
    const cols = count > 0 ? Math.min(configured, count) : configured;
    if (cols === 1) {
      return {
        '--listings-per-row': '1',
        'grid-template-columns': 'minmax(0, 380px)',
        'justify-content': 'center',
      };
    }
    return {
      '--listings-per-row': String(cols),
      'grid-template-columns': `repeat(${cols}, minmax(0, 1fr))`,
    };
  }

  /** @deprecated Prefer {@link productsGridStyle} with item count. */
  productsGridColumns(perRow: number | null | undefined, itemCount?: number | null): string {
    return this.productsGridStyle(perRow, itemCount)['grid-template-columns'];
  }

  private featuredUrlsOrDefault(value: string[] | string | null | undefined): string[] {
    const parts: string[] = [];
    if (Array.isArray(value)) {
      parts.push(...value);
    } else if (typeof value === 'string') {
      parts.push(...value.split(/[,\n]/));
    }
    const out: string[] = [];
    const seen = new Set<string>();
    for (const part of parts) {
      let slug = (part || '').trim().toLowerCase();
      if (slug.startsWith('/')) {
        slug = slug.slice(1);
      }
      if (slug.endsWith('/')) {
        slug = slug.slice(0, -1);
      }
      if (!slug || slug.includes('/') || slug.length > 64 || !/^[a-z0-9._~-]+$/.test(slug)) {
        continue;
      }
      if (seen.has(slug)) {
        continue;
      }
      seen.add(slug);
      out.push(slug);
      if (out.length >= 24) {
        break;
      }
    }
    return out;
  }

  private sectionsOrDefault(raw: HomePageSection[] | null | undefined): HomePageSection[] {
    const allowed = new Set<HomeSectionType>(['hero', 'categories', 'latest', 'banner', 'richtext']);
    const unique = new Set<HomeSectionType>(['hero', 'categories', 'latest']);
    const seenUnique = new Set<string>();
    const seenIds = new Set<string>();
    const out: HomePageSection[] = [];
    let idx = 0;
    for (const item of raw || []) {
      if (out.length >= 12) {
        break;
      }
      const type = String(item?.type || '').toLowerCase() as HomeSectionType;
      if (!allowed.has(type)) {
        continue;
      }
      if (unique.has(type) && seenUnique.has(type)) {
        continue;
      }
      if (unique.has(type)) {
        seenUnique.add(type);
      }
      let id = String(item?.id || '').trim().toLowerCase();
      if (!/^[a-z0-9_-]{1,40}$/.test(id) || seenIds.has(id)) {
        id = `${type}-${++idx}`;
        while (seenIds.has(id)) {
          id = `${type}-${++idx}`;
        }
      }
      seenIds.add(id);
      out.push({
        id,
        type,
        enabled: item?.enabled !== false,
        props: this.sectionPropsOrDefault(type, item?.props),
      });
    }
    for (const required of ['hero', 'categories', 'latest'] as HomeSectionType[]) {
      if (!seenUnique.has(required)) {
        out.push({id: required, type: required, enabled: true, props: {}});
      }
    }
    return out.length ? out : DEFAULT_HOME_SECTIONS.map(s => ({...s, props: {...s.props}}));
  }

  private sectionPropsOrDefault(type: HomeSectionType, raw: Record<string, string> | null | undefined): Record<string, string> {
    if (type === 'banner') {
      return {
        title: this.copyOrDefault(raw?.['title'], DEFAULT_BANNER_PROPS.title, 160),
        subtitle: this.copyOrDefault(raw?.['subtitle'], DEFAULT_BANNER_PROPS.subtitle, 280),
        ctaLabel: this.copyOrDefault(raw?.['ctaLabel'], DEFAULT_BANNER_PROPS.ctaLabel, 48),
        ctaLink: this.browseLinkOrDefault(raw?.['ctaLink']),
        backgroundColor: this.hexOrDefault(raw?.['backgroundColor'], DEFAULT_BANNER_PROPS.backgroundColor),
      };
    }
    if (type === 'richtext') {
      const body = String(raw?.['body'] || '')
        .replace(/[<>]/g, ' ')
        .replace(/\r\t/g, ' ')
        .trim();
      return {
        title: this.copyOrDefault(raw?.['title'], DEFAULT_RICHTEXT_PROPS.title, 160),
        body: (body || DEFAULT_RICHTEXT_PROPS.body).slice(0, 2000),
      };
    }
    return {};
  }

  private nameOrDefault(value: string | null | undefined): string {
    const trimmed = (value || '').trim().replace(/\s+/g, ' ');
    if (!trimmed) {
      return DEFAULT_BRAND_THEME.marketplaceName;
    }
    return trimmed.slice(0, 48);
  }

  private fontOrDefault(value: string | null | undefined): string {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return DEFAULT_BRAND_THEME.fontFamily;
    }
    const byId = BRAND_FONT_OPTIONS.find(opt => opt.id === trimmed);
    if (byId) {
      return byId.css;
    }
    const byCss = BRAND_FONT_OPTIONS.find(opt => opt.css === trimmed);
    return byCss?.css || DEFAULT_BRAND_THEME.fontFamily;
  }

  private hexOrDefault(value: string | null | undefined, fallback: string): string {
    const trimmed = (value || '').trim();
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
      return trimmed.length === 4
        ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toLowerCase()
        : trimmed.toLowerCase();
    }
    return fallback;
  }

  private imageUrlOrEmpty(value: string | null | undefined): string {
    const trimmed = (value || '').trim();
    if (!trimmed || trimmed.length > 500) {
      return '';
    }
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
      return '';
    }
    if (trimmed.includes('..')) {
      return '';
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    if (/^\/[a-zA-Z0-9._~/-]+$/.test(trimmed)) {
      return trimmed;
    }
    return '';
  }

  private optionalHexOrEmpty(value: string | null | undefined): string {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return '';
    }
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
      return trimmed.length === 4
        ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toLowerCase()
        : trimmed.toLowerCase();
    }
    return '';
  }
}
